// ============================================================================
// Facebook Page — Graph API error → SocialPostingError mapping.
//
// Maps every documented Meta error code to the 8-code social-posting taxonomy
// defined in `capabilities/social-posting.ts`. Adapters must NEVER throw
// across the capability boundary, so this is the canonical translation point.
//
// Code reference: https://developers.facebook.com/docs/graph-api/guides/error-handling
//
// Decision tree:
//   - Code 190 (token issues)                     → `auth`
//   - Code 200, 10, 102, 459, 467                 → `permission`
//   - Code 4, 17, 32, 613 + HTTP 429              → `rate_limit`
//   - Code 100 with media subcodes                → `media_invalid`
//   - Code 368, 396, 1404006, 1404007             → `provider_rejected`
//   - HTTP 5xx, code 1, code 2                    → `transient`
//   - Anything else                                → `unknown`
// ============================================================================

import type {
  SocialPostingError,
  SocialPostingErrorCode,
} from '../../capabilities/social-posting'
import type {
  FacebookPageErrorEnvelope,
  FacebookPageGraphError,
} from './types'

// ============================================================================
// FacebookPageError — adapter-internal Error class (use sparingly)
// ============================================================================

/**
 * Used only when the adapter needs to throw INSIDE a helper to unwind a
 * partial flow. Public surface still returns `SocialResult<T>`; this class
 * is converted to a `SocialPostingError` at the boundary.
 */
export class FacebookPageError extends Error {
  public readonly name = 'FacebookPageError'
  constructor(
    public readonly code: SocialPostingErrorCode,
    message: string,
    public readonly details: {
      providerCode?: number
      providerSubcode?: number
      providerTraceId?: string
      httpStatus?: number
      retryAfterSeconds?: number
    } = {},
  ) {
    super(message)
  }
}

// ============================================================================
// classifyFacebookPageError
// ============================================================================

/**
 * Input bundle for the classifier — keeps the call sites tidy. All fields
 * optional so the helper degrades gracefully when the provider returns
 * non-JSON / network failure.
 */
export interface ClassifyFacebookPageErrorInput {
  envelope?: FacebookPageErrorEnvelope | null
  httpStatus?: number
  /** Parsed Retry-After header value (seconds). */
  retryAfterSeconds?: number
  /** Raw value of the `X-App-Usage` response header (Meta rate-limit hint). */
  appUsageHeader?: string | null
  /** Optional message override when we don't have an envelope. */
  fallbackMessage?: string
}

/**
 * Map a Meta Graph error to a `SocialPostingError`. Always returns — never
 * throws. Adapter code wraps the result in `{ ok: false, error }`.
 *
 * Logging rule: the returned `message` is sanitised — no token text inside.
 * Callers can include the `providerTraceId` in support tickets, but the
 * adapter must NOT include the raw token even in `details`.
 */
export function classifyFacebookPageError(
  input: ClassifyFacebookPageErrorInput,
): SocialPostingError {
  const env = input.envelope?.error ?? null
  const httpStatus = input.httpStatus
  const providerCode = env?.code
  const providerSubcode = env?.error_subcode

  // Sanitise the message: Meta echoes the request URL (with access_token)
  // into the error message body. Strip before letting it through.
  const rawMessage = env?.message ?? input.fallbackMessage ?? defaultMessageFor(httpStatus)
  const message = sanitiseMetaErrorString(rawMessage)
  const providerTraceId = env?.fbtrace_id

  // --- 1. Transport-level / 5xx → transient -------------------------------
  if (typeof httpStatus === 'number' && httpStatus >= 500 && httpStatus < 600) {
    return mkError('transient', message, {
      retryable: true,
      retryAfterSeconds: input.retryAfterSeconds,
      providerTraceId,
    })
  }

  // --- 2. Rate-limit detection -------------------------------------------
  // Two signals:
  //   a. Documented codes: 4 (app limit), 17 (user limit), 32 (page limit),
  //      613 (custom limit).
  //   b. HTTP 429 from any path (FB occasionally returns 429 without a
  //      structured envelope).
  if (
    httpStatus === 429 ||
    providerCode === 4 ||
    providerCode === 17 ||
    providerCode === 32 ||
    providerCode === 613 ||
    providerSubcode === 2018
  ) {
    return mkError('rate_limit', message, {
      retryable: true,
      retryAfterSeconds:
        input.retryAfterSeconds ?? extractRetryFromAppUsage(input.appUsageHeader),
      providerTraceId,
    })
  }

  // --- 3. Auth (token revoked / expired / app uninstalled) ---------------
  if (providerCode === 190) {
    return mkError('auth', message, {
      retryable: false,
      providerTraceId,
    })
  }

  // --- 4. Permission denied (scope or page role) -------------------------
  // Code 200 is the canonical "missing permission". 10, 102 (session
  // expired but app-side), 459, 467 (login required) are documented
  // sibling codes that all indicate the caller doesn't currently have the
  // required scope/role.
  if (
    providerCode === 200 ||
    providerCode === 10 ||
    providerCode === 102 ||
    providerCode === 459 ||
    providerCode === 467
  ) {
    return mkError('permission', message, {
      retryable: false,
      providerTraceId,
    })
  }

  // --- 5. Media validation ------------------------------------------------
  // Code 100 ("Invalid parameter") with media-related subcodes is the
  // canonical media-invalid path. Common subcodes: 1366046 (image too
  // small), 1366047 (image too large), 2207013/2207014 (video format/size).
  if (providerCode === 100 && isMediaSubcode(providerSubcode)) {
    return mkError('media_invalid', message, {
      retryable: false,
      providerTraceId,
    })
  }
  // Also map raw 100 when the message text explicitly mentions media/photo
  // shape — Meta surfaces some validation paths without a subcode.
  if (providerCode === 100 && looksLikeMediaMessage(message)) {
    return mkError('media_invalid', message, {
      retryable: false,
      providerTraceId,
    })
  }

  // --- 6. Provider rejection (policy / content blocked) -------------------
  // 368 ("blocked due to spam"), 396 ("Cannot post link"), 1404006/7 (post
  // content violates policy).
  if (
    providerCode === 368 ||
    providerCode === 396 ||
    providerCode === 1404006 ||
    providerCode === 1404007
  ) {
    return mkError('provider_rejected', message, {
      retryable: false,
      providerTraceId,
    })
  }

  // --- 7. Transient catch — code 1 / 2 (unknown / service unavailable) ---
  if (providerCode === 1 || providerCode === 2) {
    return mkError('transient', message, {
      retryable: true,
      retryAfterSeconds: input.retryAfterSeconds,
      providerTraceId,
    })
  }

  // --- 8. Fallback -------------------------------------------------------
  return mkError('unknown', message, {
    retryable: false,
    providerTraceId,
  })
}

// ============================================================================
// Helpers
// ============================================================================

function mkError(
  code: SocialPostingErrorCode,
  message: string,
  extras: {
    retryable: boolean
    retryAfterSeconds?: number
    providerTraceId?: string
  },
): SocialPostingError {
  const out: SocialPostingError = {
    code,
    message,
    retryable: extras.retryable,
  }
  if (typeof extras.retryAfterSeconds === 'number') {
    out.retryAfterSeconds = extras.retryAfterSeconds
  }
  if (typeof extras.providerTraceId === 'string' && extras.providerTraceId.length > 0) {
    out.providerTraceId = extras.providerTraceId
  }
  return out
}

function defaultMessageFor(httpStatus: number | undefined): string {
  if (typeof httpStatus !== 'number') return 'Facebook Graph API error'
  if (httpStatus >= 500) return `Facebook Graph upstream error (HTTP ${httpStatus})`
  if (httpStatus === 429) return 'Facebook Graph rate limit hit (HTTP 429)'
  if (httpStatus === 401) return 'Facebook Graph authentication failed (HTTP 401)'
  if (httpStatus === 403) return 'Facebook Graph permission denied (HTTP 403)'
  return `Facebook Graph API error (HTTP ${httpStatus})`
}

/** Subcodes Meta has documented as media-validation errors. */
const MEDIA_SUBCODES = new Set<number>([
  // Image format / shape
  1366046, 1366047, 1366048, 1366049,
  // Video format / duration
  2207013, 2207014, 2207015, 2207020, 2207052,
  // Carousel media
  1404102, 1404103,
])

function isMediaSubcode(sub: number | undefined): boolean {
  if (typeof sub !== 'number') return false
  return MEDIA_SUBCODES.has(sub)
}

function looksLikeMediaMessage(msg: string): boolean {
  if (typeof msg !== 'string') return false
  const m = msg.toLowerCase()
  return (
    m.includes('image') ||
    m.includes('photo') ||
    m.includes('video') ||
    m.includes('media') ||
    m.includes('attachment') ||
    m.includes('mime')
  )
}

/**
 * Strip Meta-shaped secrets out of an error message string. Mirrors
 * `services/redacted-logger.ts → redactString` rules. Kept inline here to
 * avoid a circular dep between the adapter and the global logger (each is
 * imported by the other in the test surface).
 */
export function sanitiseMetaErrorString(raw: string): string {
  if (typeof raw !== 'string') return ''
  return raw
    .replace(/(access_token=)[^&\s"']+/gi, '$1***redacted***')
    .replace(/(client_secret=)[^&\s"']+/gi, '$1***redacted***')
    .replace(/(fb_exchange_token=)[^&\s"']+/gi, '$1***redacted***')
    .replace(/(Authorization:\s*Bearer\s+)[A-Za-z0-9._\-|]+/gi, '$1***redacted***')
    .replace(/(Bearer\s+)[A-Za-z0-9._\-|]+/gi, '$1***redacted***')
}

/**
 * Parse Meta's `X-App-Usage` header for a hint on how long to wait. The
 * header carries a JSON object like
 *   `{ "call_count": 100, "total_cputime": 12, "total_time": 25 }`
 * — when any percentage is at 100, we wait until next hour. Conservatively
 * returns a fixed 60s back-off when any field is ≥ 95.
 *
 * Reference: https://developers.facebook.com/docs/graph-api/overview/rate-limiting
 */
export function extractRetryFromAppUsage(
  headerValue: string | null | undefined,
): number | undefined {
  if (!headerValue) return undefined
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(headerValue) as Record<string, unknown>
  } catch {
    return undefined
  }
  const max = Math.max(
    toNumber(parsed.call_count),
    toNumber(parsed.total_cputime),
    toNumber(parsed.total_time),
  )
  if (!Number.isFinite(max)) return undefined
  if (max >= 100) return 3600 // back off an hour
  if (max >= 95) return 600
  if (max >= 75) return 120
  return undefined
}

function toNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  return -Infinity
}

/**
 * Parse a `Retry-After` header value (seconds or HTTP-date) into integer
 * seconds. Returns `undefined` on unparseable input so callers can fall
 * through to `X-App-Usage` heuristics.
 */
export function parseRetryAfterSeconds(
  headerValue: string | null | undefined,
): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber)
  }
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diffMs = parsed - Date.now()
    return diffMs > 0 ? Math.round(diffMs / 1000) : 0
  }
  return undefined
}

// ============================================================================
// errorFromResponse — pull envelope + headers + classify in one call
// ============================================================================

/**
 * Convenience wrapper around `classifyFacebookPageError` that also pulls the
 * envelope + relevant headers off a fetch `Response`. Adapter call sites use
 * this directly.
 */
export async function errorFromResponse(
  response: Response,
  fallbackMessage?: string,
): Promise<SocialPostingError> {
  let envelope: FacebookPageErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as FacebookPageErrorEnvelope
  } catch {
    envelope = null
  }
  return classifyFacebookPageError({
    envelope,
    httpStatus: response.status,
    retryAfterSeconds: parseRetryAfterSeconds(response.headers.get('Retry-After')),
    appUsageHeader: response.headers.get('X-App-Usage') ?? response.headers.get('x-app-usage'),
    fallbackMessage,
  })
}

// Re-export the raw graph error type so adapter consumers can pin to it.
export type { FacebookPageGraphError, FacebookPageErrorEnvelope }
